import { useContext, useMemo, useState } from 'react';
import clsx from 'clsx';
import * as R from 'ramda';

import RenameModal from '@atom/components/common/RenameModal';
import addItemIcon from '@atom/components/common/svgIcons/add_subItem.svg';
import TextOverflowTooltip from '@atom/components/common/tooltip/TextOverflowTooltip';
import WorkOrderAssetViewContext from '@atom/components/common/workOrderDetail/workOrderAssetView/WorkOrderAssetViewContext';
import { Icon, IconButton, Menu, Modal } from '@atom/mui';
import colors from '@atom/styles/colors';
import fonts from '@atom/styles/fonts';
import { AttributeGroupsItem, ChangeType } from '@atom/types/inventory';
import { WorkOrderAssetTreeElement } from '@atom/types/work';
import api from '@atom/utilities/api';
import { assetTreeHasDownstreamChanges } from '@atom/utilities/assetUtilities';
import attributeDisplayUtility from '@atom/utilities/attributeDisplayUtility';
import { hasRolePermissions, ROLE_SETS } from '@atom/utilities/authUtilities';
import { WORK_ORDERS_ENDPOINT } from '@atom/utilities/endpoints';
import iconUtilities from '@atom/utilities/iconUtilities';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import AddElementModal from './addElementModal/AddElementModal';
import ElementControls from './ElementControls';

import classes from './workOrderAssetTree.module.css';

const { MenuItem } = Menu;

const PADDING_OFFSET = 2;

const styles = {
  blankStyle: {
    boxSizing: 'border-box',
    padding: '24px',
  },
};

interface Props {
  element: WorkOrderAssetTreeElement;
  indentation: number;
}

enum ModalType {
  ADD_ELEMENT = 'ADD_ELEMENT',
  EDIT = 'EDIT',
  DELETE = 'DELETE',
  NONE = 'NONE',
}

type OpenModal = { type: ModalType; id: string | null };

const ElementRow = ({ element, indentation }: Props) => {
  const {
    workOrderDetail,
    workOrderAsset,
    refetchWorkOrderAssetTree,
    setActiveElementId,
    activeElementId,
    previewView,
    canAddAsset,
  } = useContext(WorkOrderAssetViewContext);

  const [expanded, setExpanded] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState<OpenModal>({
    type: ModalType.NONE,
    id: null,
  });

  const hasPendingChanges = useMemo(() => {
    return (
      !!element.changeType &&
      element.changeType !== ChangeType.ELEMENTS_CHANGED &&
      element.changeType !== ChangeType.PARENT_ADDED
    );
  }, [element.changeType]);

  const hasParentAdded = useMemo(() => {
    return element.changeType === ChangeType.PARENT_ADDED;
  }, [element.changeType]);

  const isDeleted = useMemo(() => {
    return element?.changeType === ChangeType.DELETED;
  }, [element]);

  const hasDownstreamChanges = useMemo(() => {
    return assetTreeHasDownstreamChanges(element);
  }, [element]);

  const showControls =
    hasRolePermissions(ROLE_SETS.MANAGER) && hasPendingChanges && !previewView;

  const showAdditionalOptions =
    hasRolePermissions(ROLE_SETS.INSPECTOR) && !workOrderDetail.isClosed;

  const arrowDirection = expanded ? 'arrow_drop_down' : 'arrow_right';

  const elementIcon = iconUtilities.getDataManagementElementIcon(
    element.markerId,
    hasPendingChanges || hasParentAdded,
  );

  const leftIcon = (
    <>
      {!R.isEmpty(element.elements) ? (
        <IconButton onClick={() => setExpanded(!expanded)}>
          <Icon>{arrowDirection}</Icon>
        </IconButton>
      ) : (
        <div style={styles.blankStyle} />
      )}
      <div className={classes.iconContainer}>
        {elementIcon}
        {hasDownstreamChanges && (
          <div className={clsx(classes.changeIndicator, classes.icon)} />
        )}
      </div>
    </>
  );

  const containerStyle = {
    paddingLeft: `calc(1rem + ${PADDING_OFFSET * indentation}rem)`,
  };

  const renameElement = async (id: string, name: string) => {
    const endpoint = `${WORK_ORDERS_ENDPOINT}/${workOrderDetail.id}/assets/${id}`;
    await api.patch(endpoint, { name });

    refetchWorkOrderAssetTree();
  };

  const deleteElement = async () => {
    const endpoint = `${WORK_ORDERS_ENDPOINT}/${workOrderDetail.id}/assets/${element.id}`;
    await api.delete(endpoint);

    setActiveElementId(workOrderAsset.id);

    refetchWorkOrderAssetTree();
  };

  const onConfirmDeleteModal = () => {
    deleteElement();
    setOpenModal({ type: ModalType.NONE, id: null });
  };

  const getAttributeSubText = (): string => {
    if (
      R.isEmpty(element.attributeGroups) ||
      R.isNil(element.attributeGroups)
    ) {
      return '';
    }

    const attributes = element.attributeGroups.reduce(
      (acc: string[], group: AttributeGroupsItem): string[] => {
        return [...acc, ...group.attributes];
      },
      [],
    );

    return attributes.reduce((acc: string, attributeId: string): string => {
      const attribute = element.attributes[attributeId];
      if (!attribute.isVisibleAsSubtext) {
        return acc;
      }

      const label = attribute.name;
      const value = attributeDisplayUtility.display(
        attribute.value,
        attribute.dataType,
        attribute.unit,
      );

      const text = `${label}: ${value}`;
      return acc ? `${acc}, ${text}` : text;
    }, '');
  };

  const isModalOpen = (modalType: ModalType) => {
    return openModal.type === modalType && openModal.id === element.id;
  };

  const attributeSubtext = getAttributeSubText();
  const subtext = isNilOrEmpty(attributeSubtext)
    ? element.assetType
    : `${element.assetType} \u00B7 ${attributeSubtext}`;

  return (
    <>
      <div
        className={clsx(
          classes.assetRow,
          element.id === activeElementId && classes.selected,
        )}
        style={containerStyle}
        onClick={() => setActiveElementId(element.id)}
      >
        <div className={classes.assetNameContainer}>
          {leftIcon}
          <div className={classes.rowNameContainer}>
            <div
              className={clsx(
                classes.rowName,
                (hasPendingChanges || hasParentAdded) && classes.blue,
              )}
            >
              <span
                className={clsx(
                  classes.rowNameText,
                  isDeleted && classes.deleted,
                )}
              >
                {element.name}
              </span>
            </div>
            <div className={clsx(classes.rowName, classes.subtext)}>
              <TextOverflowTooltip
                text={subtext}
                width="100%"
                bold={false}
                fontSize={fonts.sm}
                color={colors.neutral.gray}
                lightTooltip
              />
            </div>
          </div>
        </div>
        <div className={classes.controlsContainer}>
          {showControls && <ElementControls element={element} />}
          {showAdditionalOptions && (
            <>
              <Menu>
                {canAddAsset && element.changeType !== ChangeType.ADDED && (
                  <MenuItem
                    startAdornment={
                      <Icon>
                        <img src={addItemIcon} />
                      </Icon>
                    }
                    onClick={() =>
                      setOpenModal({
                        type: ModalType.ADD_ELEMENT,
                        id: element.id,
                      })
                    }
                  >
                    Add Sub Items
                  </MenuItem>
                )}
                <MenuItem
                  startAdornment={<Icon>edit</Icon>}
                  onClick={() =>
                    setOpenModal({ type: ModalType.EDIT, id: element.id })
                  }
                >
                  Edit
                </MenuItem>
                {element.isNew && element.changeType !== ChangeType.DELETED && (
                  <MenuItem
                    startAdornment={<Icon>delete</Icon>}
                    onClick={() =>
                      setOpenModal({
                        type: ModalType.DELETE,
                        id: element.id,
                      })
                    }
                  >
                    Delete
                  </MenuItem>
                )}
              </Menu>
              {element.changeType !== ChangeType.ADDED && (
                <AddElementModal
                  open={isModalOpen(ModalType.ADD_ELEMENT)}
                  closeModal={() =>
                    setOpenModal({ type: ModalType.NONE, id: null })
                  }
                  element={element}
                />
              )}
              <RenameModal
                open={isModalOpen(ModalType.EDIT)}
                closeModal={() =>
                  setOpenModal({ type: ModalType.NONE, id: null })
                }
                id={element.id}
                name={element.name}
                type="element"
                renameAction={renameElement}
              />
              {element.isNew && element.changeType !== ChangeType.DELETED && (
                <Modal
                  open={isModalOpen(ModalType.DELETE)}
                  title="Delete Element"
                  onCancel={() => setOpenModal({ type: null, id: null })}
                  confirmButtonText="Delete"
                  ConfirmButtonProps={{
                    sx: { background: colors.brand.red },
                  }}
                  onConfirm={onConfirmDeleteModal}
                >
                  Are you sure you want to delete this element?
                </Modal>
              )}
            </>
          )}
        </div>
      </div>
      {expanded ? (
        element.elements.map((elem: WorkOrderAssetTreeElement) => {
          return (
            <ElementRow
              key={elem.id}
              element={elem}
              indentation={indentation + 1}
            />
          );
        })
      ) : (
        <div />
      )}
    </>
  );
};

export default ElementRow;
